import { cn } from '../utils/cn';

interface FlagIconProps {
  code: string;
  className?: string;
}

// Regional Indicator Symbol Letter A (U+1F1E6) - ASCII 'A' (65).
const REGIONAL_INDICATOR_OFFSET = 0x1f1e6 - 65;

/**
 * Convert an ISO-3166-1 alpha-2 country code into the Unicode Regional
 * Indicator Symbol pair that represents its flag (e.g. "AR" -> "🇦🇷").
 *
 * The pair renders as a real flag on iOS, Android and macOS, and as the two
 * regional-indicator letters (a stylized "AR") on Windows — Microsoft
 * intentionally omits flag glyphs from Segoe UI Emoji. We rely on this
 * platform-native fallback instead of polyfilling, so the chip blends with
 * the surrounding typography on every OS.
 */
const isoToFlagEmoji = (code: string): string => {
  if (!code || code.length !== 2) return code;
  const upper = code.toUpperCase();
  return String.fromCodePoint(
    upper.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET,
    upper.charCodeAt(1) + REGIONAL_INDICATOR_OFFSET,
  );
};

/**
 * Renders a country flag using the OS-native emoji glyph. Size and color are
 * inherited from the surrounding text — control them with `text-*` utilities
 * on the parent rather than props on this component.
 */
export const FlagIcon = ({ code, className }: FlagIconProps) => {
  return (
    <span
      role="img"
      aria-label={`${code} flag`}
      className={cn('inline-block leading-none', className)}
    >
      {isoToFlagEmoji(code)}
    </span>
  );
};
